// src/utils/localData.js
//
// Every browser-storage key Friendships Vibe writes, in one list, so "Delete
// All My Data" can remove all of it and a test can tell us when someone adds
// a key this list doesn't know about (localData.test.js scans the source).
//
// Enumerated from the code on 2 October 2026. If you add a key, add it here:
// the drift test fails until you do.
//
// What is NOT in here, on purpose:
//   - Firebase's own storage (the sign-in session in IndexedDB,
//     `firebaseLocalStorageDb`, and the installation and heartbeat databases
//     the Firebase SDK keeps). The app never writes those itself; signing out
//     is what removes the session, and none of them hold anything a person
//     wrote. Deleting them under a live SDK connection blocks or corrupts it.
//   - Nothing else: the app writes no IndexedDB, Cache Storage or cookies of
//     its own.
//
// Australian English throughout.

// Exact keys in localStorage.
export const LOCAL_KEYS = [
  // Identity and settings (PrivacyAuthContext, login.html, signup.html,
  // StandaloneSignup).
  'vibe_user_identity',
  'vibe_user_settings',
  'vibe_auth_success',
  'vibe_user_id',
  'vibe_user',
  // zustand persist stores (progressStore, circlesStore). The first holds
  // which modules you have finished and the insights saved from the
  // dashboard.
  'friendships-vibe-progress',
  'friend-circles-storage',
  // Legacy progress and the removed gamification (October 2026): the old
  // zustand store and the older blob. utils/legacyProgress.js moves what is
  // worth keeping out of them and removes them on load; they stay listed so
  // a browser that hasn't loaded the app since is still wiped clean.
  'friendships-vibe-gamification',
  'vibeGamificationData',
  'vibeUserProgress',
  // Guide and Nana choices (GuideContext).
  'friendships_vibe_guide',
  'friendships_vibe_nana',
  // The reader's country for helplines (constants/crisisSupport.js).
  'friendships-vibe_crisis_country',
  // Install prompt (PWAInstallPrompt).
  'pwa-prompt-dismissed',
  'pwa-remind-later',
  'pwa-installed',
  // Hand-offs between pages.
  'selectedTool',
  'selectedToolMode',
  'selectedModule',
  'returnToModule',
  'returnToExercises',
  'continuelearning',
  // Tip of the day's old "read today" tick (it gated +5 XP; no longer
  // written, removed on load by utils/legacyProgress.js).
  'tipOfTheDay_lastRead',
  'tipOfTheDay_lastReadId',
  // The eight tools' flat keys from before per-account scoping
  // (utils/toolStorage.js LEGACY_TOOL_KEYS); migrated on first read, but an
  // old browser can still hold them.
  'socialBatteryData',
  'groupDynamicsData',
  'conflictSimulatorData',
  'digitalDramaShieldData',
  'friendshipGriefData',
  'peopleFinderData',
  'boundaryBlueprintData',
  'socialPainLabData',
];

// Key prefixes in localStorage. Each covers a family of keys built at run
// time, usually with a user id in them.
export const LOCAL_PREFIXES = [
  // Tool entries, per account: fv:tool:<uid|anon>:<tool> (grief entries,
  // unsent letters, pain logs, named friends...).
  'fv:',
  // Vibe Check results and history, progress, preferences, self-talk and
  // letters, per account: vibe_assessment_results_<uid>, vibe_progress_data_<uid>...
  'vibe_',
  // Per-account blob of the removed gamification: vibeGamificationData_<uid>
  // (no longer written, removed on load).
  'vibeGamificationData_',
  // Storage service namespace, module part progress and reading position,
  // per account: friendships-vibe_<uid>_module-parts_<n>, friendships-vibe_<uid>_...
  'friendships-vibe_',
  // Older storage service namespaces.
  'friendships_',
  'friendshipsVibe_',
  // Exercise ticks from the old module viewer, which awarded XP for them:
  // module_<n>_exercises (no longer written; removed on load by
  // utils/legacyProgress.js).
  'module_',
];

// sessionStorage (cleared when the tab closes anyway, but cleared here too).
export const SESSION_KEYS = ['revisionContext'];
export const SESSION_PREFIXES = ['fv:'];

const matches = (key, keys, prefixes) =>
  typeof key === 'string' && (keys.includes(key) || prefixes.some((prefix) => key.startsWith(prefix)));

/** True when a localStorage key is one this app writes. */
export const isAppLocalKey = (key) => matches(key, LOCAL_KEYS, LOCAL_PREFIXES);

/** True when a sessionStorage key is one this app writes. */
export const isAppSessionKey = (key) => matches(key, SESSION_KEYS, SESSION_PREFIXES);

const keysOf = (store) => {
  const keys = [];
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key !== null) keys.push(key);
    }
  } catch {
    // Storage disabled: nothing to list.
  }
  return keys;
};

const removeMatching = (store, test) => {
  const removed = [];
  if (!store) return removed;
  for (const key of keysOf(store)) {
    if (!test(key)) continue;
    try {
      store.removeItem(key);
      removed.push(key);
    } catch {
      // Keep going: one stuck key must not stop the rest being removed.
    }
  }
  return removed;
};

const defaultStore = (name) => {
  try {
    return typeof window !== 'undefined' ? window[name] : globalThis[name];
  } catch {
    return undefined;
  }
};

/**
 * Remove everything this app keeps in this browser's localStorage and
 * sessionStorage. Never throws.
 *
 * Returns { removed: [keys], remaining: [app keys still present] }. `remaining`
 * should always be empty; the caller reports it rather than claiming a clean
 * sweep when it isn't one.
 */
export function clearAllLocalData({
  local = defaultStore('localStorage'),
  session = defaultStore('sessionStorage'),
} = {}) {
  const removed = [
    ...removeMatching(local, isAppLocalKey),
    ...removeMatching(session, isAppSessionKey),
  ];
  const remaining = [
    ...(local ? keysOf(local).filter(isAppLocalKey) : []),
    ...(session ? keysOf(session).filter(isAppSessionKey) : []),
  ];
  return { removed, remaining };
}

/**
 * Remove one account's toolkit entries (and anything left under the signed-out
 * `anon` scope or the old flat keys) from this browser. Used on sign-out,
 * where other accounts' entries on a shared device are not ours to touch.
 */
export function clearToolScopes(uids = [], { local = defaultStore('localStorage') } = {}) {
  const scopes = [...new Set([...uids.filter(Boolean).map(String), 'anon'])];
  const flat = LOCAL_KEYS.slice(LOCAL_KEYS.indexOf('socialBatteryData'));
  return removeMatching(local, (key) =>
    scopes.some((scope) => key.startsWith(`fv:tool:${scope}:`)) || flat.includes(key));
}

export default { clearAllLocalData, clearToolScopes, isAppLocalKey, isAppSessionKey };
